import React, { useState, useCallback } from "react";
import { Post, PostStatus } from "@/core/types/postTypes";
import { useAppStore } from "@/core/store/useAppStore";
import { apiClient } from "@/services/apiClient";
import { DownloadIcon, SpinnerIcon, CheckIcon } from "@/components/icons";

interface DownloadPostButtonProps {
  post: Post;
  compact?: boolean;
  pill?: boolean;
  className?: string;
}

export const DownloadPostButton: React.FC<DownloadPostButtonProps> = ({
  post,
  compact = false,
  pill = false,
  className = "",
}) => {
  const { selectedClient } = useAppStore();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const canDownload =
    post.status === PostStatus.READY ||
    post.status === PostStatus.SCHEDULED ||
    post.status === PostStatus.PARTIALLY_PUBLISHED ||
    post.status === PostStatus.PUBLISHED;

  const preparationStatus = post.postFolderPreparationStatus || 'pending';
  const isPrepared = preparationStatus === 'ready';

  const handleDownloadClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      if (!selectedClient || !isPrepared) {
        return;
      }

      setIsDownloading(true);

      try {
        const response = await apiClient.post<{
          download_url: string;
          file_count: number;
          zip_size_bytes: number;
        }>(
          `/api/social-media/posts/${post.id}/download`,
          {
            clientId: selectedClient.id,
          }
        );

        const { download_url } = response;

        const link = document.createElement("a");
        link.href = download_url;
        link.download = `${post.id}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setDownloadSuccess(true);
        setTimeout(() => {
          setDownloadSuccess(false);
        }, 2000);
      } catch (error) {
        console.error("Failed to download post:", error);
      } finally {
        setIsDownloading(false);
      }
    },
    [post, selectedClient, isPrepared]
  );

  if (!canDownload) {
    return null;
  }

  if (pill) {
    return (
      <button
        onClick={handleDownloadClick}
        disabled={isDownloading || !selectedClient || !isPrepared}
        className={`p-1.5 border rounded-full flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          downloadSuccess
            ? "bg-green-100 border-green-300 text-green-700"
            : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600"
        } ${className}`}
        title={isPrepared ? "Download prepared post files" : "Post folder not prepared yet"}
      >
        {isDownloading ? (
          <SpinnerIcon className="w-3.5 h-3.5" />
        ) : downloadSuccess ? (
          <CheckIcon className="w-3.5 h-3.5" />
        ) : (
          <DownloadIcon className="w-3.5 h-3.5" />
        )}
      </button>
    );
  }

  if (compact) {
    return (
      <button
        onClick={handleDownloadClick}
        disabled={isDownloading || !selectedClient || !isPrepared}
        className={`p-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        title={isPrepared ? "Download prepared post files" : "Post folder not prepared yet"}
      >
        {isDownloading ? (
          <svg
            className="w-4 h-4 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        ) : downloadSuccess ? (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        ) : (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        )}
      </button>
    );
  }

  return (
    <button
      onClick={handleDownloadClick}
      disabled={isDownloading || !selectedClient || !isPrepared}
      className={`w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${className}`}
    >
      {isDownloading ? (
        <>
          <svg
            className="w-4 h-4 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Downloading...
        </>
      ) : downloadSuccess ? (
        <>
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          Downloaded
        </>
      ) : (
        <>
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          Download Post
        </>
      )}
    </button>
  );
};
